import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Plus,
  Folder,
  Clock,
  Trash2,
  PanelLeftClose,
  PanelLeftOpen,
  Users,
} from 'lucide-react';
import PixelLogo from '../components/PixelLogo';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EXAMPLE_IDEAS = [
  'iOS and Android habit tracker mobile app',
  'Marketing website for a design studio',
  'Invoice web app for freelancers',
];

const DashboardPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [idea, setIdea] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth >= 768);
  const [building, setBuilding] = useState(false);
  const [buildError, setBuildError] = useState('');

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    const savedPrompt = localStorage.getItem('1prompt_saved_prompt');
    if (savedPrompt) {
      localStorage.removeItem('1prompt_saved_prompt');
      setIdea(savedPrompt);
    }
  }, []);

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/projects`, {
        withCredentials: true,
      });
      setProjects(res.data);
    } catch (err) {
      console.error('Failed to fetch projects:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBuild = async () => {
    if (!idea.trim() || idea.trim().length < 8 || building) return;

    setBuilding(true);
    setBuildError('');
    try {
      const name = idea.length > 40 ? idea.substring(0, 40) + '...' : idea;
      const res = await axios.post(
        `${BACKEND_URL}/api/projects`,
        {
          name,
          idea: idea.trim(),
          status: 'building',
          chat_messages: [{ role: 'user', content: idea.trim() }],
        },
        { withCredentials: true }
      );
      const project = res.data;
      navigate(`/chat/${project.project_id}`);
    } catch (err) {
      console.error('Failed to create project:', err);
      setBuildError(err.response?.data?.detail || 'ProductOS could not start this build. Please retry.');
    } finally {
      setBuilding(false);
    }
  };

  const handleDeleteProject = async (projectId, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${BACKEND_URL}/api/projects/${projectId}`, {
        withCredentials: true,
      });
      setProjects((prev) => prev.filter((p) => p.project_id !== projectId));
    } catch (err) {
      console.error('Failed to delete project:', err);
    }
  };

  const handleLogout = async () => {
    navigate('/');
    await logout();
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    return `${diffDays}d ago`;
  };

  return (
    <div className="h-screen bg-[#F8FAFC] text-[#0F172A] flex overflow-hidden" data-testid="dashboard-page">
      {/* Left Sidebar */}
      <aside
        className={`fixed md:relative z-40 flex-shrink-0 h-screen border-r border-[#E2E8F0] bg-white flex flex-col transition-all duration-200 ${sidebarOpen ? 'w-[240px]' : 'w-0 overflow-hidden border-r-0'}`}
      >
        {/* Logo */}
        <div className="flex items-center justify-between px-5 h-12 border-b border-[#E2E8F0] min-w-[240px]">
          <div className="flex items-center gap-2">
            <PixelLogo size={16} color="#2563EB" />
            <span className="text-sm font-bold text-[#0F172A]">prompt</span>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="p-1 rounded-md hover:bg-[#F8FAFC] text-[#94A3B8] hover:text-[#475569] transition-colors"
            title="Close sidebar"
          >
            <PanelLeftClose className="w-4 h-4" />
          </button>
        </div>

        {/* New App button */}
        <div className="px-3 pt-3 pb-1">
          <button
            onClick={() => {
              setIdea('');
              window.scrollTo(0, 0);
            }}
            data-testid="new-app-button"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-sm font-semibold transition-colors"
            style={{ boxShadow: '0 4px 14px 0 rgba(37, 99, 235, 0.39)' }}
          >
            <Plus className="w-4 h-4" />
            New App
          </button>
        </div>

        {/* Project list */}
        <div className="flex-1 overflow-y-auto px-3 pt-4">
          <div className="flex items-center gap-1.5 px-2 mb-2">
            <Folder className="w-3 h-3 text-[#94A3B8]" />
            <span className="text-[10px] font-bold text-[#94A3B8] uppercase tracking-widest">
              Explore
            </span>
          </div>
          <button
            onClick={() => navigate('/agents')}
            className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[#475569] hover:bg-[#F8FAFC] hover:text-[#0F172A] transition-colors text-xs font-semibold mb-3"
          >
            <Users className="w-3.5 h-3.5" />
            Meet the Team
          </button>
          <div className="flex items-center gap-1.5 px-2 mb-2">
            <Folder className="w-3 h-3 text-[#94A3B8]" />
            <span className="text-[10px] font-bold text-[#94A3B8] uppercase tracking-widest">
              Projects
            </span>
          </div>
          <div className="space-y-0.5">
            {loading ? (
              <div className="text-xs text-[#94A3B8] px-2 py-3">Loading...</div>
            ) : projects.length === 0 ? (
              <div className="text-xs text-[#94A3B8] px-2 py-3">No projects yet</div>
            ) : (
              projects.map((project) => (
                <div
                  key={project.project_id}
                  onClick={() => navigate(`/chat/${project.project_id}`)}
                  data-testid={`sidebar-project-${project.project_id}`}
                  className="group flex items-center gap-2.5 px-2.5 py-2 rounded-lg cursor-pointer text-[#475569] hover:bg-[#F8FAFC] hover:text-[#0F172A] transition-colors"
                >
                  <div
                    className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                      project.status === 'completed'
                        ? 'bg-[#10B981]'
                        : project.status === 'building'
                        ? 'bg-amber-400 animate-pulse'
                        : project.status === 'error'
                        ? 'bg-[#EF4444]'
                        : 'bg-[#CBD5E1]'
                    }`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold truncate">{project.name}</div>
                    <div className="text-[10px] text-[#94A3B8] flex items-center gap-1 mt-0.5">
                      <Clock className="w-2.5 h-2.5" />
                      {formatDate(project.created_at)}
                    </div>
                  </div>
                  <button
                    onClick={(e) => handleDeleteProject(project.project_id, e)}
                    data-testid={`delete-project-${project.project_id}`}
                    className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-[#EF4444]/10 transition-all"
                  >
                    <Trash2 className="w-3 h-3 text-[#94A3B8] hover:text-[#EF4444]" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Bottom — User row */}
        <div className="border-t border-[#E2E8F0] p-3">
          <button
            onClick={() => navigate('/settings/profile')}
            className="w-full flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-[#F8FAFC] transition-colors"
          >
            <div className="w-6 h-6 rounded-full bg-[#2563EB] flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0">
              {user?.name?.charAt(0) || 'U'}
            </div>
            <div className="flex-1 min-w-0 text-left">
              <div className="text-xs font-semibold text-[#0F172A] truncate leading-tight">
                {user?.name || 'User'}
              </div>
              <div className="text-[10px] text-[#94A3B8] truncate leading-tight">
                {user?.email || ''}
              </div>
            </div>
          </button>
        </div>
      </aside>

      {/* Center — Build prompt */}
      <main className="flex-1 min-w-0 flex flex-col items-center justify-center px-4 md:px-8 relative grid-bg">
        {/* Sidebar toggle when closed */}
        {!sidebarOpen && (
          <button
            onClick={() => setSidebarOpen(true)}
            className="absolute top-3 left-3 p-1.5 rounded-md text-[#94A3B8] hover:text-[#475569] transition-colors"
            title="Open sidebar"
            style={{
              background: 'rgba(255,255,255,0.65)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.8)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            }}
          >
            <PanelLeftOpen className="w-4 h-4" />
          </button>
        )}
        <div className="w-full max-w-2xl space-y-8">
          <div className="text-center space-y-2">
            <h1
              className="text-4xl sm:text-5xl font-extrabold text-[#0F172A] tracking-[-0.02em]"
              data-testid="dashboard-heading"
            >
              What will you <span className="text-[#2563EB]">build?</span>
            </h1>
            <p className="text-sm text-[#475569]">One prompt in. One working mobile app, website, or web app out.</p>
          </div>

          <motion.div
            initial={{ y: 12, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="bg-white border border-[#E2E8F0] rounded-xl p-5"
            style={{ boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -2px rgba(0,0,0,0.05)' }}
            data-testid="build-input-card"
          >
            <textarea
              value={idea}
              onChange={(e) => setIdea(e.target.value.slice(0, 500))}
              placeholder="Describe the mobile app, website, or web app you want..."
              rows={3}
              data-testid="build-input-textarea"
              className="w-full bg-transparent text-[#0F172A] text-base leading-relaxed resize-none outline-none placeholder:text-[#94A3B8]"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  handleBuild();
                }
              }}
            />
            <div className="mt-3 flex items-center justify-end">
              <div className="flex items-center gap-3">
                {idea.length > 0 && (
                  <span className="text-xs text-[#94A3B8]">{idea.length}/500</span>
                )}
                <button
                  onClick={handleBuild}
                  disabled={idea.trim().length < 8 || building}
                  data-testid="build-button"
                  className="px-5 py-2 rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8] disabled:bg-[#E2E8F0] disabled:text-[#94A3B8] disabled:cursor-not-allowed text-white text-sm font-semibold transition-all"
                  style={{ boxShadow: idea.trim().length >= 8 ? '0 4px 14px 0 rgba(37, 99, 235, 0.39)' : 'none' }}
                >
                  {building ? 'Starting in ProductOS…' : 'Build'}
                </button>
              </div>
            </div>
          </motion.div>

          {buildError && <p className="text-center text-sm text-red-600" role="alert">{buildError}</p>}

          <div className="flex flex-wrap gap-2 justify-center">
            {EXAMPLE_IDEAS.map((ex) => (
              <button
                key={ex}
                onClick={() => setIdea(ex)}
                data-testid={`example-idea-${ex.split(' ').slice(0, 2).join('-').toLowerCase()}`}
                className="px-3 py-1.5 rounded-full text-[11px] font-semibold text-[#475569] hover:text-[#2563EB] transition-all"
                style={{
                  background: 'rgba(255,255,255,0.6)',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.85)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                }}
              >
                {ex}
              </button>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default DashboardPage;
